/**
 * ConfirmDialog — shared two-button confirmation prompt (cancel/secondary + a colored confirm
 * action), used for anything that shouldn't fire on a single click: clearing a hold, reassigning
 * a document, releasing a reservation, etc.
 */

import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

interface ConfirmDialogProps {
  open: boolean;
  titleId: string;
  title: React.ReactNode;
  message: React.ReactNode;
  /** Disables both buttons and lets the confirm label show progress text. */
  busy?: boolean;
  cancelLabel?: string;
  onCancel: () => void;
  confirmLabel: React.ReactNode;
  confirmColor?: 'primary' | 'warning' | 'error';
  onConfirm: () => void | Promise<void>;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  titleId,
  title,
  message,
  busy = false,
  cancelLabel = 'Cancel',
  onCancel,
  confirmLabel,
  confirmColor = 'primary',
  onConfirm,
}) => (
  <Dialog open={open} onClose={busy ? undefined : onCancel} aria-labelledby={titleId}>
    <DialogTitle id={titleId}>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText component="div">{message}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={busy}>
        {cancelLabel}
      </Button>
      <Button onClick={onConfirm} color={confirmColor} variant="contained" disableElevation disabled={busy}>
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);
